import { useState } from 'preact/hooks';
import { useStore } from '../store';
import { fmtMoney } from '../bridge';
import { FENCE_PRICE_PER_M_CENTS } from '../../sim/systems/landtools';
import { penBoundaryEdges, type Pen } from '../../sim/spatial/fences';
import type { UiEnclosure, UiEnclosureType } from '../bridge';

/**
 * Facilities, enclosure upkeep (upgrade, repair, convert), camping and timeshare sales.
 * Costs for upgrade, repair and convert use the same formulas as the sim over the projected enclosure catalog.
 * Facility costs and effects are not projected yet, so they are described as placeholders and the sim charges on build.
 */
const WRAP = 'padding:6px 18px 16px';
const CARD = 'border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px';
const NOTE = 'white-space:normal;text-transform:none;letter-spacing:0.01em';
const SMALL = 'padding:5px 12px;font-size:12px';
const label = (s: string): string => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const FENCE_STEP_M = 50;

/** PROPOSED copy; ids come from the sim's facility list, effects are the sim's placeholder values. */
const FACILITIES: Array<{ id: string; name: string; text: string }> = [
  { id: 'vet_clinic', name: 'Vet clinic', text: 'Sick animals recover faster each day (placeholder effect).' },
  { id: 'feed_silo', name: 'Feed silo', text: 'Animals stay fed a little longer between feedings (placeholder effect).' },
  { id: 'viewing_platform', name: 'Viewing platform', text: 'Hunt booking quotes rise about 10% (placeholder effect).' },
];

// 4A: fence is grade-priced (type-independent), so conversion carries no fence
// rate delta - base rework fee + positive water delta only, mirroring the sim.
const GRADE_NAMES = ['', 'Basic', 'Heavy', 'Predator-proof'];

function conversionCost(e: UiEnclosure, cur: UiEnclosureType, tgt: UiEnclosureType): number {
  const wd = tgt.water_point_cost_cents - cur.water_point_cost_cents;
  return tgt.base_cost_cents + (wd > 0 ? wd * e.waterPoints : 0);
}


/** P106 1A re-grade preview - mirrors the sim charge exactly (per-edge Math.round;
 *  old grade 0 = full new rate, no salvage credit; edges already at target free).
 *  Also lists NEIGHBOR claimed pens whose weakest link changes via shared edges. */
function regradePreview(sim: ReturnType<typeof useStore>['sim'], enc: UiEnclosure, target: number): { totalCents: number; neighbors: string[] } | null {
  const newRate = FENCE_PRICE_PER_M_CENTS[target] as number | undefined;
  if (newRate === undefined) return null;
  const delta = (old: number, metres: number): number => {
    const oldRate = old >= 1 ? (FENCE_PRICE_PER_M_CENTS[old] ?? 0) : 0;
    return Math.round(metres * (newRate - oldRate * 40 / 100));
  };
  if (!Object.hasOwn(sim.penCells, enc.id)) {
    const old = enc.fenceGrade ?? 0;
    if (old === target) return null;
    return { totalCents: delta(old, enc.fencePerimeterM), neighbors: [] };
  }
  const layout = sim.fenceLayouts[enc.parcelId] as import('../bridge').UiFenceLayout | undefined;
  const cells = sim.penCells[enc.id] as number[] | undefined;
  if (!layout || !cells) return null;
  const gradeOf = (key: string): number => {
    const i = layout.segments.indexOf(key);
    return i >= 0 ? (layout.grades?.[i] ?? 0) : 0;
  };
  const boundary = penBoundaryEdges(layout, { cells } as Pen);
  let total = 0;
  const changed = new Set<string>();
  for (const key of boundary) {
    const old = gradeOf(key);
    if (old === target) continue;
    total += delta(old, layout.edgeM);
    changed.add(key);
  }
  if (changed.size === 0) return null;
  const neighbors: string[] = [];
  for (const other of sim.enclosures) {
    if (other.id === enc.id || other.parcelId !== enc.parcelId) continue;
    const otherCells = sim.penCells[other.id] as number[] | undefined;
    if (!otherCells) continue;
    const otherBoundary = penBoundaryEdges(layout, { cells: otherCells } as Pen);
    if (!otherBoundary.some((k) => changed.has(k))) continue;
    let weakest = 3;
    for (const k of otherBoundary) weakest = Math.min(weakest, changed.has(k) ? target : gradeOf(k));
    const oldWeakest = other.fenceGrade ?? 0;
    if (weakest !== oldWeakest) neighbors.push(`${label(other.type)} ${other.id}: weakest link ${GRADE_NAMES[oldWeakest] || 'ungraded'} -> ${GRADE_NAMES[target]}`);
  }
  return { totalCents: total, neighbors };
}

export function FacilitiesPanel() {
  const { sim, bridge } = useStore();
  const [encPick, setEncPick] = useState('');
  const [regradePick, setRegradePick] = useState(0);
  const [parcelPick, setParcelPick] = useState('');
  const built = sim.facilitiesBuilt;
  const enc = sim.enclosures.find((e) => e.id === encPick) ?? sim.enclosures.at(0);
  const cur = enc ? sim.enclosureTypes[enc.type] : undefined;
  const integrity = enc ? sim.business?.fences?.find((f) => f.id === enc.id)?.integrityBp : undefined;
  const owned = sim.parcels.filter((p) => p.owned);
  const camp = sim.business?.camping;
  const campParcels = camp ? camp.parcels : owned.map((p) => ({ parcelId: p.id, minSafety: 0, allowed: true }));
  const parcelId = parcelPick || campParcels.at(0)?.parcelId || '';
  const campOk = campParcels.find((p) => p.parcelId === parcelId)?.allowed !== false;
  const [timeshareReview,setTimeshareReview]=useState<string|null>(null);
  const ts = sim.business?.timeshare?.units;
  const units = ts ? ts.map((u) => ({ id: u.unitId, kind: u.kind, parcelId: u.parcelId })) : (sim.standing?.lodging?.unitList ?? []);
  const sold = ts ? ts.filter((u) => u.sold).map((u) => u.unitId) : (sim.business?.timeshare?.soldUnitIds ?? []);
  const sel = 'flex:1;min-width:0;padding:8px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft)';
  return (
    <div style={WRAP}>
      <div class="eyebrow eyebrow-rule">Facilities</div>
      {(sim.facilityCatalog ?? FACILITIES.map((f) => ({ id: f.id, costCents: -1, effectText: f.text }))).map((c) => {
        const f = { id: c.id, name: label(c.id), text: c.effectText + (c.costCents < 0 ? ' (placeholder effect)' : '') };
        const has = built?.includes(f.id) === true;
        return (
          <div key={f.id} class="card" style={CARD}>
            <div style="display:flex;align-items:center;gap:8px">
              <span class="row-main"><div class="row-value">{f.name}</div><div class="row-label" style={NOTE}>{f.text}{c.costCents >= 0 ? ` - $${fmtMoney(c.costCents)}` : ''}</div></span>
              <button class="btn btn-quiet" style={SMALL} disabled={has} onClick={() => { bridge.send('facility.build', { facilityId: f.id }); }}>{has ? 'Built' : 'Build'}</button>
            </div>
          </div>
        );
      })}
      <div class="row-label" style={`${NOTE};margin-top:6px`}>The sim rejects a build if cash is short. Costs and effects are placeholders until balance is tuned.</div>

      <div class="eyebrow eyebrow-rule" style="margin-top:14px">Enclosure upkeep</div>
      {!enc && <div class="row-label" style="margin-top:6px">Build an enclosure in Park first.</div>}
      {enc && (
        <>
          <select aria-label="Enclosure" style={`${sel};width:100%;margin-top:8px`} value={enc.id} onChange={(e) => { setEncPick(e.currentTarget.value); }}>
            {sim.enclosures.map((e) => <option key={e.id} value={e.id}>{label(e.type)} - {e.areaHa} ha - {e.id}</option>)}
          </select>
          <div class="row-label" style={`${NOTE};margin-top:6px`}>{label(enc.type)}: {enc.fencePerimeterM} m of fence, {enc.waterPoints} water {enc.waterPoints === 1 ? 'point' : 'points'}, {enc.animalCount} animal{enc.animalCount === 1 ? '' : 's'}.{integrity !== undefined ? ` Fence condition ${Math.round(integrity / 100)}%.` : ' Fence condition not provided by the sim yet.'}</div>
          {cur && (
            <div class="card" style={CARD}>
              <div class="row-value">Upgrade</div>
              <div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap">
                {(enc.fenceGrade ?? 0) > 0 ? (
                  <button class="btn btn-quiet" style={SMALL} onClick={() => { bridge.send('enclosure.upgrade', { enclosureId: enc.id, addFencePerimeterM: FENCE_STEP_M }); }}>+{FENCE_STEP_M} m fence - ${fmtMoney((FENCE_PRICE_PER_M_CENTS[enc.fenceGrade ?? 0] ?? 0) * FENCE_STEP_M)} ({GRADE_NAMES[enc.fenceGrade ?? 0]})</button>
                ) : (
                  // 4A: a legacy ungraded fence must pick a grade for the new metres (sets the fence's grade)
                  GRADE_NAMES.slice(1).map((name, i) => {
                    const g = i + 1;
                    return <button key={g} class="btn btn-quiet" style={SMALL} onClick={() => { bridge.send('enclosure.upgrade', { enclosureId: enc.id, addFencePerimeterM: FENCE_STEP_M, fenceGrade: g }); }}>+{FENCE_STEP_M} m {name} - ${fmtMoney((FENCE_PRICE_PER_M_CENTS[g] ?? 0) * FENCE_STEP_M)}</button>;
                  })
                )}
                <button class="btn btn-quiet" style={SMALL} onClick={() => { bridge.send('enclosure.upgrade', { enclosureId: enc.id, addWaterPoints: 1 }); }}>+1 water point - ${fmtMoney(cur.water_point_cost_cents)}</button>
              </div>
              <div class="row-label" style={`${NOTE};margin-top:6px`}>More fence costs more to patrol and repair; water points help the herd hold up in dry spells.</div>
            </div>
          )}
          {cur && (
            <div class="card" style={CARD}>
              <div class="row-value">Shrink - 40% salvage refund</div>
              <div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap">
                {!Object.hasOwn(sim.penCells, enc.id) && (enc.fenceGrade ?? 0) > 0 && (
                  <button class="btn btn-quiet" style={SMALL} disabled={enc.fencePerimeterM < FENCE_STEP_M} onClick={() => { bridge.send('enclosure.shrink', { enclosureId: enc.id, removeFencePerimeterM: FENCE_STEP_M }); }}>-{FENCE_STEP_M} m fence - refund ${fmtMoney(Math.round(FENCE_STEP_M * (FENCE_PRICE_PER_M_CENTS[enc.fenceGrade ?? 0] ?? 0) * 40 / 100))}</button>
                )}
                <button class="btn btn-quiet" style={SMALL} disabled={enc.waterPoints < 1} onClick={() => { bridge.send('enclosure.shrink', { enclosureId: enc.id, removeWaterPoints: 1 }); }}>-1 water point - refund ${fmtMoney(Math.round(cur.water_point_cost_cents * 40 / 100))}</button>
              </div>
              <div class="row-label" style={`${NOTE};margin-top:6px`}>{Object.hasOwn(sim.penCells, enc.id) ? 'This pen was drawn on the map. Removing fence there breaks the loop, dissolves the pen and frees the animals; resizing a drawn pen is not available yet.' : 'Salvage pays back 40% of material cost. Too few water points stresses the herd in dry spells.'}</div>
            </div>
          )}
          {cur && (() => {
            const old = enc.fenceGrade ?? 0;
            const targets = [1, 2, 3].filter((g) => g !== old);
            const preview = regradePick > 0 ? regradePreview(sim, enc, regradePick) : null;
            return (
              <div class="card" style={CARD}>
                <div class="row-value">Change fence grade</div>
                <div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap">
                  {targets.map((g) => {
                    const pv = regradePreview(sim, enc, g);
                    if (!pv) return null;
                    return <button key={g} class="btn btn-quiet" style={SMALL} onClick={() => { setRegradePick(g); }}>{GRADE_NAMES[g]} - {pv.totalCents >= 0 ? `$${fmtMoney(pv.totalCents)}` : `refund $${fmtMoney(-pv.totalCents)}`}</button>;
                  })}
                </div>
                {preview && regradePick > 0 && (
                  <div class="card" style="border:var(--ui-border);margin-top:8px;padding:8px 12px" data-testid="regrade-confirm">
                    <div class="row-label" style={NOTE}>
                      Re-grade to {GRADE_NAMES[regradePick]}: {preview.totalCents >= 0 ? `charge $${fmtMoney(preview.totalCents)}` : `refund $${fmtMoney(-preview.totalCents)}`} (new material - 40% salvage of old; fences at the new grade already cost nothing{old === 0 ? '; this unrecorded legacy fence gets no salvage credit' : ''}).
                    </div>
                    {preview.neighbors.map((n) => <div key={n} class="row-label" style={`${NOTE};color:var(--ui-ink-soft)`}>Shared fence: {n}</div>)}
                    <div style="display:flex;gap:8px;margin-top:8px">
                      <button class="btn btn-primary" style={SMALL} onClick={() => { bridge.send('enclosure.regrade', { enclosureId: enc.id, targetGrade: regradePick }); setRegradePick(0); }}>Confirm</button>
                      <button class="btn btn-quiet" style={SMALL} onClick={() => { setRegradePick(0); }}>Cancel</button>
                    </div>
                  </div>
                )}
                <div class="row-label" style={`${NOTE};margin-top:6px`}>Replacement is atomic: the whole boundary changes grade at once. Shared edges re-price here too, so a neighboring pen can get stronger.</div>
              </div>
            );
          })()}
          <div class="card" style={CARD}>
            <div class="row-value">Repair fence</div>
            <div class="row-label" style={NOTE}>{integrity === undefined ? 'Condition is not projected, the sim rejects the repair if the fence is intact.' : integrity >= 10000 ? 'Fence is intact.' : `Cost scales with the damage: ${cur ? `$${fmtMoney(Math.floor((((enc.fenceGrade ?? 0) > 0 ? (FENCE_PRICE_PER_M_CENTS[enc.fenceGrade ?? 0] ?? 0) : cur.fence_cost_per_m_cents) * enc.fencePerimeterM * (10000 - integrity)) / 10000))}` : 'set by the sim'}.`}</div>
            {(enc.fenceGrade ?? 0) > 0 ? (
              <button class="btn btn-quiet" style={`${SMALL};margin-top:8px`} disabled={integrity !== undefined && integrity >= 10000} onClick={() => { bridge.send('enclosure.repair', { enclosureId: enc.id }); }}>Repair</button>
            ) : (
              // 4A: repair materials are new purchases - an ungraded fence picks a grade first
              <div style="display:flex;gap:6px;margin-top:8px;flex-wrap:wrap">
                {GRADE_NAMES.slice(1).map((name, i) => {
                  const g = i + 1;
                  return <button key={g} class="btn btn-quiet" style={SMALL} disabled={integrity !== undefined && integrity >= 10000} onClick={() => { bridge.send('enclosure.repair', { enclosureId: enc.id, fenceGrade: g }); }}>Repair as {name}</button>;
                })}
              </div>
            )}
          </div>
          {cur && (
            <div class="card" style={CARD}>
              <div class="row-value">Convert to another type</div>
              <div class="row-label" style={NOTE}>Every animal inside must be housable in the new type or the sim rejects it.</div>
              {Object.entries(sim.enclosureTypes).filter(([id]) => id !== enc.type).map(([id, t]) => (
                <div key={id} style="display:flex;align-items:center;gap:8px;margin-top:8px">
                  <span class="row-main"><div class="row-value" style="font-size:15px">{label(id)}</div><div class="row-label">${fmtMoney(conversionCost(enc, cur, t))}</div></span>
                  <button class="btn btn-quiet" style={SMALL} onClick={() => { bridge.send('enclosure.convert', { enclosureId: enc.id, targetType: id }); }}>Convert</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div class="eyebrow eyebrow-rule" style="margin-top:14px">Camping and timeshares</div>
      <div class="card" style={CARD}>
        <div class="row-value">Build a campsite</div>
        <div class="row-label" style={NOTE}>Cheap lodging placed on one of your parcels.{camp ? ` A parcel needs safety ${camp.minBuildSafety} or more.` : ' The sim rejects parcels where camping is not allowed.'}</div>
        {campParcels.length === 0 && <div class="row-label" style="margin-top:6px">You own no parcels yet.</div>}
        {campParcels.length > 0 && (
          <div style="display:flex;gap:8px;margin-top:8px">
            <select aria-label="Parcel for campsite" style={sel} value={parcelId} onChange={(e) => { setParcelPick(e.currentTarget.value); }}>{campParcels.map((p) => <option key={p.parcelId} value={p.parcelId}>{p.parcelId}{camp ? ` - safety ${p.minSafety}${p.allowed ? '' : ' (too low)'}` : ''}</option>)}</select>
            <button class="btn btn-quiet" style={SMALL} disabled={!campOk} onClick={() => { bridge.send('camp.build', { parcelId }); }}>Build</button>
          </div>
        )}
      </div>
      <div class="card" style={CARD}>
        <div class="row-value">Sell a lodging unit as a timeshare</div>
        <div class="row-label" style={NOTE}>Selling gives the ranch upfront cash plus an owner fee received daily, not a fee the ranch owes. The sold unit stops walk-in guest billing; existing daily upkeep still applies. Units sold: {sold.length}.</div>
        {units.length === 0 && <div class="row-label" style="margin-top:6px">No lodging units built yet.</div>}
        {units.map((u) => {const terms=ts?.find(x=>x.unitId===u.id),blocked=terms?.refusal===undefined?'Current sale eligibility unavailable.':terms.refusal;return (
          <div key={u.id} style="margin-top:8px;border-top:var(--ui-border);padding-top:8px">
            <div class="row-value" style="font-size:15px">{label(u.kind)} {u.id}</div><p style={NOTE}>{u.parcelId?`Parcel ${u.parcelId}. `:''}{terms?`Receive $${fmtMoney(terms.upfrontCents)} now and $${fmtMoney(terms.feeCentsYear)}/year owner fee, posted daily as $${(Math.floor(terms.feeCentsYear/(terms.daysPerYear??365))/100).toFixed(2)}. Existing upkeep $${fmtMoney(terms.upkeepCentsDay??0)}/day remains.`:'Current terms unavailable.'}</p>
            {blocked&&<p style={NOTE}>Cannot sell now: {blocked}.</p>}<button class="btn btn-quiet" style={SMALL} disabled={sold.includes(u.id)||blocked!==null} onClick={()=>{setTimeshareReview(u.id);}}>{sold.includes(u.id)?'Sold':'Review this unit'}</button>
            {timeshareReview===u.id&&terms&&<div><p style={NOTE}>Sell exactly {u.id} as a timeshare for $${fmtMoney(terms.upfrontCents)} now? The owner fee is income to this ranch, while normal guest billing stops and upkeep continues. No buyback or cancellation option is offered by the current rules. Values are provisional.</p><button class="btn btn-primary" disabled={blocked!==null} onClick={()=>{bridge.send('timeshare.sell',{unitId:u.id});}}>Confirm this timeshare sale</button><button class="btn btn-quiet" onClick={()=>{setTimeshareReview(null);}}>Not now</button></div>}
          </div>
        );})}
      </div>
    </div>
  );
}
